import React, { useEffect, useMemo, useState } from 'react';
import Caixa from './Caixa';
import Text from './Text';
import TroncCalculations from './TroncCalculations';
import { applyTimeZone, fetchAPI, getSubdomain } from '../../utils/utils';
import SETTINGS from '../../SETTINGS';
import moment from 'moment';

const COLLA = getSubdomain();

function PrintableTab({ tabName, json, posicions, castellersInfo, assistenciesEvent, targetAssistencies, arribenTard, surtenAviat, headerInfo, nomsColumnesSim, addons, selectedEvent, selectedBundle, selectedVersio }) {
    // Reserve space for TroncCalculations elements (duplicated boxes, fustes, column names)
    const extraBottomSpace = useMemo(() => {
        if (tabName.toLowerCase() !== 'tronc' || ['falconsbcn', 'falconsvila', 'falconspiera', 'falconsvilanova'].includes(COLLA)) return 0;

        // Get all boxes with a pilar assigned
        const boxes = Object.values(json).filter(b => b.type === 'caixa' && (b.pilar || b.pilar === 0));
        
        // Group by pilar
        const pilars = {};
        boxes.forEach(box => {
            if (!pilars[box.pilar]) pilars[box.pilar] = [];
            pilars[box.pilar].push(box);
        });

        // Calculate max height needed
        let maxPixels = 0;
        
        Object.values(pilars).forEach(pilarBoxes => {
            // Boxes that will be duplicated (not in current tab)
            const duplicatedBoxes = pilarBoxes.filter(box => box.pestanya?.toLowerCase() !== tabName.toLowerCase());
            
            if (duplicatedBoxes.length > 0) {
                // Calculate height for this pilar
                const pilarHeight = duplicatedBoxes.reduce((sum, box) => {
                    const h = box.box.width < box.box.height ? box.box.width : box.box.height;
                    return sum + h + 10; // + gap
                }, 0);
                
                maxPixels = Math.max(maxPixels, pilarHeight);
            }
        });

        // Add space for Fustes (~30px) and NomColumna (~50px) + padding
        return maxPixels > 0 ? maxPixels + 100 : 0;
    }, [json, tabName]);

    // Filter items for this tab
    const caixes_json = Object.entries(json)
        .filter(([key, val]) => val.type === 'caixa')
        .filter(([key, val]) => (COLLA === 'falconsbcn' || COLLA === 'falconsvila' || COLLA === 'falconspiera' || COLLA === 'falconsvilanova') || (!val?.pestanya && tabName.toLowerCase() === 'pinya') || val?.pestanya === tabName.toLowerCase());
    
    const textos_json = Object.entries(json)
        .filter(([key, val]) => val.type === 'text')
        .filter(([key, val]) => (COLLA === 'falconsbcn' || COLLA === 'falconsvila' || COLLA === 'falconspiera' || COLLA === 'falconsvilanova') || (!val?.pestanya && tabName.toLowerCase() === 'pinya') || val?.pestanya === tabName.toLowerCase())
    
    if (caixes_json.length === 0) return null;

    const caixes_keys = caixes_json.map(el => el[0]);

    // Calculate bounding box for this tab
    const caixesPositions = caixes_json
        .filter(([key, val]) => val?.box?.transform)
        .map(([key, val]) => val.box.transform)
        .map(([a, b, c, d, e, f]) => [e, f]);

    if (caixesPositions.length === 0) return null;

    const { minX, maxX, minY, maxY } = caixesPositions.reduce((acc, [x, y]) => ({
        minX: Math.min(acc.minX, x),
        maxX: Math.max(acc.maxX, x),
        minY: Math.min(acc.minY, y),
        maxY: Math.max(acc.maxY, y)
    }), { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity });

    const width = maxX - minX;
    const height = maxY - minY + extraBottomSpace;
    const centerX = minX + width / 2;
    const centerY = minY + height / 2;

    // Page dimensions (A4 landscape approx in px at screen 96dpi)
    const PAGE_WIDTH = 1000;
    const PAGE_HEIGHT = 700;

    const scale = Math.min(
        PAGE_WIDTH / (width + 100), // Add padding
        PAGE_HEIGHT / (height + 100)
    );

    const caixes = caixes_keys.map(caixa_id => {
        const val = json[caixa_id];
        const assignat =
            'clone' in val && val['clone'] in posicions.caixes ? posicions.caixes[val['clone']] :
            caixa_id in posicions.caixes ? posicions.caixes[caixa_id] :
            -1;

        const targetAssistencia = targetAssistencies
            ?.find(el => el.id === assignat)?.assistencia
            || "No confirmat";

        const isEscaleta = val?.escala_a?.length > 0;

        const assistencia = assistenciesEvent
            ?.filter(assistencia => assistencia.id === assignat)
            ?.map(assistencia => assistencia['assistència'])
            ?.[0];

        const fullAssistencia = assistenciesEvent
            ?.find(assistencia => assistencia.id === assignat);

        return (
            <Caixa
                key={caixa_id}
                {...val}
                id={caixa_id}
                boxProps={val["box"]}
                textProps={val["text"]}
                className="noselectable"
                fill={val.fill || 'white'}
                assignat={assignat}
                hasClone={'clone' in val}
                fontSize={14}
                targetAssistencia={targetAssistencia}
                showUntil={9999} // Always show all in print
                isEscaleta={isEscaleta}
                assistencia={assistencia}
                opacity={1}
                fullAssistencia={fullAssistencia}
                readonly={true}
                arribenTard={arribenTard}
                surtenAviat={surtenAviat}
                castellersInfo={castellersInfo}
                caixaSelected={-1}
                json={json} // Required for memo comparison
                targetEvent={null} // or pass if needed
            />
        );
    });

    const textos = textos_json.map(([text_id, val]) => {
        // Check if text is within or close to the bounding box
        if (!val.box || !val.box.transform) return null;
        const [a, b, c, d, x, y] = val.box.transform;
        
        // Simple check: if within expanded bounds
        if (x >= minX - 200 && x <= maxX + 200 && y >= minY - 200 && y <= maxY + 200) {
             return (
                <Text
                    key={text_id}
                    id={text_id}
                    textValue={val.value}
                    boxProps={val.box}
                    textProps={val.text}
                />
            );
        }
        return null;
    });
    
    const count = caixes_keys
        .map(caixa_id => {
             const val = json[caixa_id];
             const assignat = 'clone' in val && val['clone'] in posicions.caixes ? posicions.caixes[val['clone']] :
                 caixa_id in posicions.caixes ? posicions.caixes[caixa_id] :
                 -1;
             return assignat > -1 ? 1 : 0;
        })
        .reduce((acc, val) => acc + val, 0);

    return (
        <div className="print-page" style={{ pageBreakAfter: 'always', position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 20, left: 20, textAlign: 'left', zIndex: 1000 }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{headerInfo.provaName}</div>
                <div style={{ fontSize: '14px' }}>{headerInfo.eventName}</div>
                <div style={{ fontSize: '14px' }}>{headerInfo.eventDate}</div>
                <div style={{ fontSize: '14px' }}>{headerInfo.collaName}</div>
            </div>
            <h2 style={{ textAlign: 'right', position: 'absolute', top: 20, right: 20, margin: 0, zIndex: 1000 }}>{tabName}</h2>
            <img 
                src={`/logos/${COLLA}.png`} 
                style={{ 
                    position: 'absolute', 
                    bottom: 20, 
                    right: 20, 
                    width: '100px', 
                    zIndex: 1000,
                    opacity: 0.3
                }} 
            />
            {/* <div
                style={{
                    position: 'absolute',
                    top: 15,
                    right: 15,
                    backgroundColor: '#d1e8ff',
                    color: '#111',
                    padding: '5px 10px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '12px',
                    zIndex: 1000,
                }}
            >
                <span style={{ fontSize: '14px', color: '#2859A8' }}>👤</span> {count}
            </div> */}
            <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: `translate(-50%, -50%) scale(${scale}) translate(${-centerX}px, ${-centerY}px)`,
                transformOrigin: 'center',
                width: 0, // Container with 0 dimensions, contents are absolute
                height: 0
            }}>
                {caixes}
                {textos}
                {
                    (COLLA !== 'falconsbcn' && COLLA !== 'falconsvila' && COLLA !== 'falconspiera' && COLLA !== 'falconsvilanova') &&
                    <TroncCalculations
                        json={json}
                        opacity={1}
                        nomsColumnesSim={nomsColumnesSim}
                        pestanya={tabName}
                        posicions={posicions}
                        castellersInfo={castellersInfo}
                        assistenciesEvent={assistenciesEvent}
                        readonly={true}
                        addons={addons}
                        selectedEvent={selectedEvent}
                        selectedBundle={selectedBundle}
                        selectedVersio={selectedVersio}
                        setAddons={() => {}}
                        arribenTard={arribenTard}
                        surtenAviat={surtenAviat}
                        targetAssistencies={targetAssistencies}
                    />
                }
            </div>
        </div>
    );
}

function PrintablePissarra({ tabs, withAddons, posicions, castellersInfo, assistenciesEvent, targetAssistencies, arribenTard, surtenAviat, selectedEventInfo, selectedBundle, selectedVersio, nomsColumnesSim, addons, selectedEvent }) {
    const [bundleName, setBundleName] = useState(selectedBundle);

    useEffect(() => {
        if (selectedBundle) {
            fetchAPI(`/bundle/${selectedBundle}`, (data) => {
                if (data && data.nom) {
                    setBundleName(data.nom);
                } else {
                    setBundleName(selectedBundle);
                }
            }, false);
        }
    }, [selectedBundle]);

    const derivedTabs = useMemo(() => {
        if (tabs && tabs.length > 0) return tabs;
        const pestanyesFound = new Set([
            ...Object.values(withAddons)
                .map(caixa => caixa.pestanya)
                .filter(pestanya => pestanya)
        ]);
        const pestanyesMatched = ['Pinya', 'Folre', 'Manilles', 'Puntals', 'Tronc', 'Organització', 'Músics', 'Altres']
            .filter(nom => pestanyesFound.has(nom.toLowerCase()));
        
        return pestanyesMatched.length > 0 ? pestanyesMatched : ['Pinya'];
    }, [tabs, withAddons]);

    const provaName = bundleName || '';
    const eventName = selectedEventInfo?.title || '';
    const eventDate = selectedEventInfo && selectedEventInfo['data-esperada-inici'] 
        ? applyTimeZone(selectedEventInfo['data-esperada-inici']).format('DD/MM/YYYY') 
        : '';
    const collaName = SETTINGS(COLLA).fullname;

    const headerInfo = {
        provaName,
        eventName,
        eventDate,
        collaName
    };

    return (
        <div className="printable-pissarra">
            {derivedTabs.map(tab => (
                <PrintableTab
                    key={tab}
                    tabName={tab}
                    json={withAddons}
                    posicions={posicions}
                    castellersInfo={castellersInfo}
                    assistenciesEvent={assistenciesEvent}
                    targetAssistencies={targetAssistencies}
                    arribenTard={arribenTard}
                    surtenAviat={surtenAviat}
                    headerInfo={headerInfo}
                    nomsColumnesSim={nomsColumnesSim}
                    addons={addons}
                    selectedEvent={selectedEvent}
                    selectedBundle={selectedBundle}
                    selectedVersio={selectedVersio}
                />
            ))}
        </div>
    );
}

export default PrintablePissarra;
